import React from 'react'
import classNames from 'classnames'
import { IJersey } from 'protos'
import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles'
import sizeStyles from './sizeStyles'

export type Size = 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge'

interface Props {
  jersey?: IJersey | null,
  size?: Size,
  className?: string
}

const sleevePath =
  'M12 16c4.25-7 6-8.38 11.5-11C29.73 2.01 45 1 45 1l19 15L83 1s14.86.72 22 4c7.63 3.5 8.63 4.5 ' +
  '12 11q6.52 12.56 10 37l-25.15 6.17L97 120s-15.93 7-32.63 7c-16.7 0-33.42-7-33.42-7l-4.73-60.83L1 ' +
  '53q4.14-25.7 11-37z'

const frontPath =
  'M23.5 24L45 4l19 15L83 4l22 19.08L97 120s-15.27 7.05-33 ' +
  '7c-16.41.02-33.05-7-33.05-7l-7.46-96z'

const collarPath =
  'M125.74 46.9q-6.09 2.14-8.74 4-2.46 1.73-3 5l12.71-3.15-.97-5.85zM2.26 47q6.09 ' +
  '2.14 8.74 4 2.46 1.73 3 5L1.29 52.85 2.26 47zM47 1l17 14L81 1h6.02L64 19 41 1h6z'

const styles = createStyles({
  ...sizeStyles,
  shadowed: {
    filter: 'drop-shadow(0 2px 1px rgba(0, 0, 0, .3))',
  },
})

const defaultBaseColor = '999'
const defaultSleeveColor = '777'
const defaultNumberColor = 'DDD'
const defaultJerseyNumber = ''

type InjectedProps = Props & WithStyles<typeof styles>

const Jersey: React.SFC<InjectedProps> = ({
  jersey,
  size,
  classes,
  className,
  ...props
}) => {
  const baseColor = (jersey && jersey.baseColor) || defaultBaseColor
  const sleeveColor = (jersey && jersey.sleeveColor) || defaultSleeveColor
  const numberColor = (jersey && jersey.numberColor) || defaultNumberColor
  const jerseyNumber = (jersey && jersey.jerseyNumber) || defaultJerseyNumber

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={classNames(className, classes.shadowed, classes[size as Size])}
      viewBox="0 0 128 128"
      width="128"
      height="128"
    >
      <defs>
        <clipPath id="a">
          <path d="M0 0h128v128H0z" />
        </clipPath>
      </defs>
      <g clipPath="url(#a)">
        <path
          d={sleevePath}
          fill={`#${sleeveColor}`}
        />
        <radialGradient
          id="b"
          fx="50%"
          fy="50%"
          gradientTransform="matrix(1.01745 0 0 1 0 -.22)"
        >
          <stop offset="0%" stopColor="#fff" />
          <stop offset="0%" />
          <stop offset="100%" stopColor="#fff" />
        </radialGradient>
        <path
          d={sleevePath}
          fill="url(#b)"
          fillOpacity=".6"
          style={{ mixBlendMode: 'multiply' }}
        />
        <path
          d={frontPath}
          fill={`#${baseColor}`}
        />
        <linearGradient id="c" x1="50%" y1="100%" x2="50%" y2="0%">
          <stop offset="0%" stopColor="#fff" />
          <stop offset="100%" />
        </linearGradient>
        <path
          d={frontPath}
          fill="url(#c)"
          fillOpacity=".2"
          style={{ mixBlendMode: 'multiply' }}
        />
        <g>
          <path
            d={collarPath}
            fill={`#${numberColor}`}
          />
          <text
            x="50%"
            y={{
              xsmall: '45%',
              small: '45%',
              medium: '47%',
              large: '44%',
              xlarge: '44%',
            }[size as Size]}
            fontFamily="Flama"
            textAnchor="middle"
            fontWeight={700}
            fontSize={{
              xsmall: 32,
              small: 32,
              medium: 30,
              large: 30,
              xlarge: 28,
            }[size as Size]}
            fill={`#${numberColor}`}
          >
            {jerseyNumber}
          </text>
        </g>
        <path
          d="M48.5 108l-7-47m38 47l7-47"
          strokeWidth={3}
          stroke="#000"
          strokeLinecap="round"
          opacity=".15"
        />
        <path
          d="M35 29.44h6"
          strokeWidth={3}
          stroke="#000"
          strokeLinecap="round"
          opacity=".4"
        />
        <path
          d="M88 29.44a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"
          opacity=".4"
        />
      </g>
    </svg>
  )
}

Jersey.defaultProps = {
  size: 'medium',
  jersey: {
    baseColor: defaultBaseColor,
    sleeveColor: defaultSleeveColor,
    numberColor: defaultNumberColor,
    jerseyNumber: defaultJerseyNumber,
  },
}

export default withStyles(styles)(Jersey)
